import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useAuth } from '@wehunt/core-web'
import type { GeoArea } from '@wehunt/ui'
import { listGroupHuntingGrounds, type HuntingGroundAreaDto } from './api/huntingGroundsApi'
import { dtoToGeoArea } from './mapDtoMappers'

export type GroupHuntingGrounds = {
  boundaryAreas: GeoArea[]
  subAreaPolygons: GeoArea[]
  forbiddenPolygons: GeoArea[]
  rawAreas: HuntingGroundAreaDto[]
}

const EMPTY: GroupHuntingGrounds = {
  boundaryAreas: [],
  subAreaPolygons: [],
  forbiddenPolygons: [],
  rawAreas: [],
}

export function useGroupHuntingGrounds(groupId: string): GroupHuntingGrounds {
  const { state } = useAuth()
  const isSignedIn = state.status === 'signed-in'

  const { data } = useQuery({
    queryKey: ['clubhouse', 'teams', groupId, 'hunting-grounds'],
    enabled: isSignedIn && !!groupId,
    queryFn: async () => {
      const token = await (state as Extract<typeof state, { status: 'signed-in' }>).user.getIdToken()
      return listGroupHuntingGrounds(groupId, token)
    },
  })

  // Derived once per ANSWER, not once per render. The team map view remounts
  // and re-frames the whole ground whenever the map object it is given changes
  // identity, and the route composes that object out of this one — so a fresh
  // array here threw the reader back to the fitted view on every unrelated
  // re-render, which is what made hiding a single pin reset their zoom.
  return useMemo(() => {
    if (!data) return EMPTY

    const rawAreas = data.areas
    return {
      rawAreas,
      boundaryAreas:     rawAreas.filter((a) => a.areaType === 'hunt').map(dtoToGeoArea),
      subAreaPolygons:   rawAreas.filter((a) => a.areaType === 'sub').map(dtoToGeoArea),
      forbiddenPolygons: rawAreas.filter((a) => a.areaType === 'forbidden').map(dtoToGeoArea),
    }
  }, [data])
}
